import { ActivatedRoute, Router } from "@angular/router";
import { Observable, combineLatest, distinctUntilChanged, filter, map } from "rxjs";

/**
 * Emits a deep-link query param's id once both it and its target row collection have loaded.
 * `loaded$` must emit `null`/`undefined` while loading and an array (possibly empty) once loaded
 * (the null-sentinel convention from SignalSubscriptionImpl), so a stale id that matches nothing
 * still emits and can be cleared instead of being retried forever on every future data refresh.
 *
 * Emits once per id: `distinctUntilChanged()` collapses the repeat emissions `loaded$` produces
 * on every downstream data tick (each source getter returns a freshly built array, so any
 * dependency change yields a new reference) — without it, a later tick re-emits the same id
 * after a caller has already cleared its selection for that id, reopening a closed panel.
 */
export function deepLinkParamReady$(
  id$: Observable<string | undefined>,
  loaded$: Observable<unknown[] | null | undefined>,
): Observable<string> {
  return combineLatest([id$, loaded$]).pipe(
    filter((pair): pair is [string, unknown[]] => !!pair[0] && Array.isArray(pair[1])),
    map(([id]) => id),
    distinctUntilChanged(),
  );
}

/** Drop a deep-link query param once it's been consumed; UI state lives elsewhere (e.g. a table signal). */
export function clearDeepLinkQueryParam(
  router: Router,
  route: ActivatedRoute,
  paramName: string,
): void {
  if (!route.snapshot.queryParamMap.get(paramName)) {
    return;
  }
  void router.navigate([], {
    relativeTo: route,
    queryParams: { [paramName]: null },
    queryParamsHandling: "merge",
    replaceUrl: true,
  });
}
